import React, { useState } from 'react';
import {
    Box, Typography, Paper, Stack, Chip, Divider,
    LinearProgress, Accordion, AccordionSummary, AccordionDetails,
    Grid, Alert
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon,
    SportsHockey as SportsHockeyIcon,
    Calculate as CalculateIcon,
    AccessTime as AccessTimeIcon,
    Place as PlaceIcon,
    CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { TournamentGame, TournamentPool } from '../../types';
import { calculateAdvancementOutlook, OutcomeScenario } from '../../utils/advancementCalculator';

interface AdvancementPredictionsCardProps {
    games: TournamentGame[];
    standings: TournamentPool[];
    tournamentKey?: string;
}

const getStatusColor = (status: OutcomeScenario['statusType']): 'success' | 'primary' | 'warning' | 'error' => {
    switch (status) {
        case 'guaranteed': return 'success';
        case 'likely': return 'primary';
        case 'bubble': return 'warning';
        case 'eliminated': return 'error';
    }
};

const getStatusLabel = (status: OutcomeScenario['statusType'], isMultiPool?: boolean): string => {
    switch (status) {
        case 'guaranteed': return isMultiPool ? 'Locks Playoff Spot' : 'Locks Top Seed';
        case 'likely': return isMultiPool ? 'High Probability Playoff' : 'High Probability Top 8';
        case 'bubble': return 'Bubble / Help Needed';
        case 'eliminated': return 'Consolation Bracket';
    }
};

export const AdvancementPredictionsCard: React.FC<AdvancementPredictionsCardProps> = ({ games, standings, tournamentKey }) => {
    const [expandedRooting, setExpandedRooting] = useState<boolean>(false);
    const outlook = calculateAdvancementOutlook(games, standings, { tournamentKey });

    if (!outlook) return null;

    const {
        jetsTeamName,
        gamesRemaining,
        currentPoints,
        currentRecord,
        currentRank,
        totalTeams,
        currentQuotient,
        goalsFor,
        goalsAgainst,
        scenarios,
        rootingGuide,
        keyTakeaway
    } = outlook;

    return (
        <Paper
            variant="outlined"
            elevation={0}
            sx={{
                mb: { xs: 2.5, sm: 4 },
                p: { xs: 1.5, sm: 3 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                borderColor: 'primary.main',
                borderWidth: 2,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TrendingUpIcon color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6" component="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                                Advancement Outlook & Playoff Scenarios
                            </Typography>
                            <Chip label="EXPERIMENTAL" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            Live round-robin scenarios for {jetsTeamName}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
                    <Chip
                        label={`Record: ${currentRecord.w}W-${currentRecord.otw}OTW-${currentRecord.otl}OTL-${currentRecord.l}L • ${currentPoints} PTS`}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}
                    />
                    <Chip
                        label={outlook.poolName && outlook.poolRank
                            ? `${outlook.poolName}: #${outlook.poolRank} of ${outlook.poolTotalTeams} • Overall: #${currentRank} of ${totalTeams}`
                            : `Rank: #${currentRank} of ${totalTeams}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}
                    />
                    <Chip
                        label={`${gamesRemaining} Game${gamesRemaining === 1 ? '' : 's'} Remaining`}
                        size="small"
                        sx={{ bgcolor: 'grey.100', fontWeight: 600, fontSize: '0.72rem' }}
                    />
                </Stack>
            </Stack>

            {/* Disclaimer & Key Takeaway Banner */}
            <Alert severity="info" icon={<SportsHockeyIcon />} sx={{ mb: 1.5, borderRadius: 1.5, p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="body2" fontWeight="600" sx={{ fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>
                    {keyTakeaway}
                </Typography>
            </Alert>

            <Alert severity="warning" variant="outlined" sx={{ mb: 2.5, py: 0.5, px: { xs: 1, sm: 1.5 }, borderRadius: 1.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                <strong>⚠️ Experimental Feature:</strong> Advancement projections and probability models are calculated for informational purposes and may not account for unexpected tiebreaker rulings or manual adjustments. Official advancement and seeding will be finalized by the tournament director upon completion of preliminary play.
            </Alert>

            {/* Scenarios Grid */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalculateIcon fontSize="small" color="action" />
                Possible Outcomes & Playoff Path:
            </Typography>

            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2.5 }}>
                {scenarios.map((scenario, idx) => {
                    const statusColor = getStatusColor(scenario.statusType);
                    return (
                        <Grid size={{ xs: 12, md: scenarios.length === 2 ? 6 : (scenarios.length === 1 ? 12 : (scenarios.length <= 4 ? 6 : 4)) }} key={idx}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: { xs: 1.25, sm: 2 },
                                    height: '100%',
                                    borderRadius: 1.5,
                                    bgcolor: scenario.statusType === 'guaranteed' ? 'success.50' : (scenario.statusType === 'likely' ? 'primary.50' : 'grey.50'),
                                    borderColor: scenario.statusType === 'guaranteed' ? 'success.light' : (scenario.statusType === 'likely' ? 'primary.light' : 'divider'),
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                            {scenario.label}
                                        </Typography>
                                        <Chip
                                            label={getStatusLabel(scenario.statusType, Boolean(outlook.poolName))}
                                            size="small"
                                            color={statusColor}
                                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                        />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                        {scenario.description} • Projected Finish: <strong>{scenario.projectedSeedRange}</strong>
                                    </Typography>

                                    {/* Playoff Qualifier Gauge */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                                            <Typography variant="caption" fontWeight="600" color="text.secondary">
                                                {outlook.poolName ? 'Sweet 16 Playoff Qualifier Chance' : 'Top 8 Playoff Qualifier Chance'}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="text.primary">
                                                {scenario.advancementChanceTop8}%
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={scenario.advancementChanceTop8}
                                            color={statusColor}
                                            sx={{ height: 6, borderRadius: 3 }}
                                        />
                                    </Box>

                                    {/* Championship Path Gauge */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {outlook.poolName ? 'Sunday Quarterfinal Path Chance' : 'Top 4 Seed Chance'}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                {scenario.advancementChanceTop4}%
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={scenario.advancementChanceTop4}
                                            sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.200' }}
                                        />
                                    </Box>

                                    {/* Requirements list */}
                                    <Box sx={{ pl: 2, mt: 1 }}>
                                        {scenario.requirements.map((req, rIdx) => (
                                            <Typography key={rIdx} variant="caption" color="text.secondary" sx={{ display: 'list-item', mb: 0.5 }}>
                                                {req}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Post-Round Robin Possible Game Times & Parent Schedule Guide */}
            {outlook.postRoundRobinOutlook && (
                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <AccessTimeIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                            Possible Game Times & Parent Schedule (Post-Round Robin)
                        </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {outlook.postRoundRobinOutlook.overviewSummary}
                    </Typography>

                    {/* Window Spans Callout Grid */}
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        {outlook.postRoundRobinOutlook.timeSpans.map((span, sIdx) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={sIdx}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        bgcolor: sIdx === 0 ? 'primary.50' : 'background.paper',
                                        borderColor: sIdx === 0 ? 'primary.main' : 'divider',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="caption" fontWeight="bold" color={sIdx === 0 ? 'primary.main' : 'text.secondary'} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                            {span.day}
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' }, mb: 0.25 }}>
                                            {span.timeSpan}
                                        </Typography>
                                        <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            {span.stage} ({span.spanDuration})
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PlaceIcon sx={{ fontSize: '0.85rem' }} color="action" />
                                            {span.locations.join(', ')}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Breakdown by Potential Finish Scenarios */}
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Detailed Game Times & Rinks by Pool Finish
                    </Typography>

                    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                        {outlook.postRoundRobinOutlook.scenarios.map((sc, scIdx) => {
                            const isPlayoff = sc.stageType === 'playoff';
                            return (
                                <Paper
                                    key={scIdx}
                                    variant="outlined"
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        borderRadius: 1.5,
                                        bgcolor: sc.chancePercent > 50 ? (isPlayoff ? 'success.50' : 'primary.50') : (sc.chancePercent === 0 ? 'grey.50' : 'background.paper'),
                                        borderColor: sc.chancePercent > 50 ? (isPlayoff ? 'success.light' : 'primary.light') : (sc.chancePercent === 0 ? 'divider' : 'grey.300'),
                                        opacity: sc.chancePercent === 0 ? 0.65 : 1
                                    }}
                                >
                                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5}>
                                        <Box sx={{ minWidth: { md: 220 } }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {sc.finishLabel}
                                                </Typography>
                                                <Chip
                                                    label={`${sc.chancePercent}% Chance`}
                                                    size="small"
                                                    color={sc.chancePercent >= 50 ? 'success' : (sc.chancePercent > 0 ? 'primary' : 'default')}
                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                                />
                                            </Stack>
                                            <Chip
                                                label={sc.stageBadge}
                                                size="small"
                                                variant="outlined"
                                                color={isPlayoff ? 'primary' : 'default'}
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                            />
                                        </Box>

                                        {/* Game 4 Details */}
                                        <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', p: 1.25, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                                                <CalendarMonthIcon sx={{ fontSize: '0.9rem' }} color="primary" />
                                                <Typography variant="body2" fontWeight="bold">
                                                    {sc.game4.dateTime}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                <span><strong>Rink:</strong> {sc.game4.location}</span>
                                                <span>•</span>
                                                <span><strong>Matchup:</strong> {sc.game4.opponent}</span>
                                                <span>({sc.game4.roundName})</span>
                                            </Typography>
                                        </Box>

                                        {/* Sunday Path If Win */}
                                        {sc.sundayPath && (
                                            <Box sx={{ minWidth: { md: 280 }, bgcolor: 'background.paper', p: 1.25, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                                <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
                                                    🏒 If Jets Win Saturday:
                                                </Typography>
                                                <Stack spacing={0.25}>
                                                    {sc.sundayPath.quarterfinal && (
                                                        <Typography variant="caption" sx={{ fontSize: '0.73rem' }}>
                                                            <strong>QF:</strong> {sc.sundayPath.quarterfinal.dateTime.replace('Sunday, Nov 8 • ', 'Sun ').replace('Sunday, Sep 13 • ', 'Sun ')} @ {sc.sundayPath.quarterfinal.location}
                                                        </Typography>
                                                    )}
                                                    {sc.sundayPath.semifinal && (
                                                        <Typography variant="caption" sx={{ fontSize: '0.73rem' }}>
                                                            <strong>SF:</strong> {sc.sundayPath.semifinal.dateTime.replace('Sunday, Nov 8 • ', 'Sun ').replace('Sunday, Sep 13 • ', 'Sun ')} @ {sc.sundayPath.semifinal.location}
                                                        </Typography>
                                                    )}
                                                    {sc.sundayPath.championship && (
                                                        <Typography variant="caption" sx={{ fontSize: '0.73rem' }}>
                                                            <strong>Final:</strong> {sc.sundayPath.championship.dateTime.replace('Sunday, Nov 8 • ', 'Sun ').replace('Sunday, Sep 13 • ', 'Sun ')} @ {sc.sundayPath.championship.location}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Box>
            )}

            {/* Tiebreaker Goal Quotient Tracker */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Tiebreaker Metrics Watch
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Goal Quotient = <code>GF / (GF + GA)</code> • Used to break 2-way and 3-way ties for seeding
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>GF</Typography>
                            <Typography variant="body2" fontWeight="bold">{goalsFor}</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>GA</Typography>
                            <Typography variant="body2" fontWeight="bold">{goalsAgainst}</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Quotient</Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {currentQuotient.toFixed(3)}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Paper>

            {/* Rooting Guide Accordion */}
            {rootingGuide && rootingGuide.length > 0 && gamesRemaining > 0 && (
                <Accordion
                    variant="outlined"
                    expanded={expandedRooting}
                    onChange={(_, isExpanded) => setExpandedRooting(isExpanded)}
                    sx={{ borderRadius: 1.5, '&:before': { display: 'none' } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <VisibilityIcon color="primary" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight="bold">
                                Jets Rooting Guide: Key Division Matchups to Watch
                            </Typography>
                            <Chip label={`${rootingGuide.length} Games`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Outcomes in these preliminary games between other teams directly impact Jets playoff seeding:
                        </Typography>
                        <Stack spacing={1.5}>
                            {rootingGuide.map((match, idx) => (
                                <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {match.teamA} vs {match.teamB}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {match.dateTime} • {match.reason}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: { sm: 'right' } }}>
                                            <Chip
                                                label={`Root For: ${match.preferredWinner}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
        </Paper>
    );
};

export default AdvancementPredictionsCard;
